// src/features/locker-controllers/components/ui/ConnectionStatusChip.tsx
import React from 'react';
import { Chip, Tooltip, ChipProps } from '@mui/material';
import { useTranslate } from 'react-admin';
import { 
  Wifi, 
  WifiOff, 
  Schedule,
  SignalWifiOff 
} from '@mui/icons-material';
import { 
  formatLastSeen,
  getConnectionStatusColor,
  isOnline 
} from '../../types';

interface ConnectionStatusChipProps {
  lastSeenAt?: string;
  size?: ChipProps['size'];
  showIcon?: boolean;
  showTooltip?: boolean;
  variant?: ChipProps['variant'];
}

const ConnectionStatusChip: React.FC<ConnectionStatusChipProps> = ({ 
  lastSeenAt, 
  size = 'small',
  showIcon = true,
  showTooltip = true,
  variant = 'filled',
}) => {
  const translate = useTranslate();
  const info = formatLastSeen(lastSeenAt);
  const online = isOnline(lastSeenAt);
  const color = getConnectionStatusColor(lastSeenAt);
  
  const getIcon = () => {
    if (!showIcon) return undefined;
    
    if (!lastSeenAt) {
      return <SignalWifiOff />;
    }
    
    if (online) {
      return <Wifi />;
    }
    
    if (info.minutesAgo < 60) {
      return <Schedule />;
    }
    
    return <WifiOff />;
  };
  
  const chip = (
    <Chip
      icon={getIcon()}
      label={info.text}
      size={size}
      color={color}
      variant={online ? 'filled' : 'outlined'}
      sx={{
        '& .MuiChip-icon': {
          fontSize: size === 'small' ? 16 : 20,
        },
      }}
    />
  );
  
  if (showTooltip && lastSeenAt) {
    return (
      <Tooltip 
        title={
          <div>
            <div>{translate('resources.locker-controllers.ui.last_connection')} {info.formattedDate}</div>
            {online && <div>{translate('resources.locker-controllers.ui.online_status')}</div>}
            {!online && info.minutesAgo < 1440 && (
              <div>{translate('resources.locker-controllers.ui.minutes_ago', { minutes: info.minutesAgo })}</div>
            )}
          </div>
        }
      >
        {chip}
      </Tooltip>
    );
  }
  
  return chip;
};

export default ConnectionStatusChip;